import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import MarkdownContainer from '../../../atoms/MarkdownContainer/MarkdownContainer';
import { getFieldType } from '../../../../domain/form/formUseCase';
import { parseFontAwesomeClass } from '../../../../utils/fontawesome/config';
import cn from '../../../../utils/cn';

import styles from './GridField.module.css';

/**
 * Resolve the non-null cell payload from the discriminated union.
 * @param {FormTableCell} cell
 * @returns {FormTableCellText | FormTableCellMarkdown | FormTableCellBoolean | FormTableCellIcon | FormTableCellImage | FormTableCellField | null}
 */
export function getCellPayload(cell) {
  if (!cell) return null;
  if (cell.text) return cell.text;
  if (cell.markdown) return cell.markdown;
  if (cell.boolean) return cell.boolean;
  if (cell.icon) return cell.icon;
  if (cell.image) return cell.image;
  if (cell.field) return cell.field;
  return null;
}

/**
 * @param {FormTableCell} cell
 * @returns {string | null}
 */
export function getCellColId(cell) {
  return getCellPayload(cell)?.colId ?? null;
}

/**
 * @param {FormTableCell} cell
 * @returns {string | null}
 */
export function getCellRowId(cell) {
  return getCellPayload(cell)?.rowId ?? null;
}

/**
 * Renders a single grid cell content (display or nested form field).
 * @param {object} props
 * @param {FormTableCell} props.cell
 * @param {(formItem: FormItem) => JSX.Element | null} props.renderNestedField
 * @param {(rowId: string, colId: string) => string | undefined} [props.getCellValue]
 * @param {string} [props.rowId]
 * @returns {JSX.Element | null}
 */
function GridCell({
  cell, renderNestedField, getCellValue, rowId,
}) {
  if (cell.text) {
    const { value, link, colId } = cell.text;
    const effectiveRowId = rowId ?? cell.text.rowId;
    const computedValue = effectiveRowId && getCellValue
      ? getCellValue(effectiveRowId, colId)
      : undefined;
    const displayValue = computedValue !== undefined ? computedValue : value;
    const content = link ? (
      <a href={link} target="_blank" rel="noopener noreferrer">{displayValue}</a>
    ) : displayValue;
    return (
      <div className={cn([styles['cell-content'], styles['cell-label']])}>
        {content}
      </div>
    );
  }

  if (cell.markdown) {
    return (
      <div className={cn([styles['cell-content'], styles['cell-label']])}>
        <MarkdownContainer content={cell.markdown.value} />
      </div>
    );
  }

  if (cell.boolean) {
    const iconName = cell.boolean.value ? cell.boolean.trueIcon : cell.boolean.falseIcon;
    const iconDefinition = iconName ? parseFontAwesomeClass(iconName) : null;
    return (
      <div className={cn([styles['cell-content'], styles['cell-label'], styles['cell-boolean']])}>
        {iconDefinition ? (
          <FontAwesomeIcon icon={/** @type {any} */ (iconDefinition)} />
        ) : null}
      </div>
    );
  }

  if (cell.icon) {
    const iconDefinition = cell.icon.icon ? parseFontAwesomeClass(cell.icon.icon) : null;
    const iconEl = iconDefinition ? (
      <FontAwesomeIcon
        icon={/** @type {any} */ (iconDefinition)}
        style={cell.icon.color ? { color: cell.icon.color } : undefined}
      />
    ) : null;
    if (cell.icon.url) {
      return (
        <div className={cn([styles['cell-content'], styles['cell-label']])}>
          <a href={cell.icon.url} target="_blank" rel="noopener noreferrer">
            {iconEl}
          </a>
        </div>
      );
    }
    return (
      <div className={cn([styles['cell-content'], styles['cell-label']])}>
        {iconEl}
      </div>
    );
  }

  if (cell.image) {
    const img = (
      <img
        src={cell.image.imageUrl}
        alt=""
        className={styles['cell-image']}
      />
    );
    return (
      <div className={cn([styles['cell-content'], styles['cell-label']])}>
        {cell.image.link ? (
          <a href={cell.image.link} target="_blank" rel="noopener noreferrer">{img}</a>
        ) : img}
      </div>
    );
  }

  if (cell.field?.formItem) {
    const { formItem } = cell.field;
    if (getFieldType(formItem) === 'grid') return null;
    return (
      <div className={cn([styles['cell-content'], styles['cell-field']])}>
        {renderNestedField(formItem)}
      </div>
    );
  }

  return null;
}

export default GridCell;
